// ============================================================
// MULTI-TENANT: src/contexts/TenantContext.js
//
// NOVA ARQUITETURA — Login-based (URL única: integrai.com)
// ─────────────────────────────────────────────────────────
// Antes: tenant detectado pelo subdomínio (bloqueava a app)
// Agora: tenant carregado APÓS login pelo tenant_id do usuário
//
// Fluxo:
//  1. App carrega → verifica se já há sessão (sessionStorage)
//  2. Se há sessão → carrega tenant pelo usuario.tenant_id
//  3. Se não há sessão → mostra tela de login (tenant = null)
//  4. Após login → useTenantAuth chama setTenantData(tenant)
//  5. Tema/cores/abas são aplicados com base no tenant carregado
//
// Exceção: subdomínio "admin" (admin.integrai.com) ativa o
// painel SuperAdmin sem exigir tenant.
// ============================================================

import React, {
  createContext, useContext, useEffect, useState, useCallback,
} from 'react';
import { isAdminSubdomain, getTenantById } from '../services/tenantService';
import supabase from '../services/supabaseClient';
import TelaCarregando from '../components/shared/TelaCarregando';

// ─── Context ─────────────────────────────────────────────────
const TenantContext = createContext(null);

// ─── Hook público ─────────────────────────────────────────────
export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant deve ser usado dentro de <TenantProvider>');
  return ctx;
}

// ─── Tela de carregamento ─────────────────────────────────────
function TenantLoadingScreen() {
  return <TelaCarregando />;
}

// ─── Provider ────────────────────────────────────────────────
export function TenantProvider({ children }) {
  const [state, setState] = useState({
    tenant:       null,
    tenantId:     null,
    loading:      true,
    isSuperAdmin: false,
  });

  // ── Inicialização: verifica admin e sessão existente ──────
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1. Subdomínio "admin" → SuperAdmin sem tenant
      if (isAdminSubdomain()) {
        if (!cancelled) {
          setState({ tenant: null, tenantId: null, loading: false, isSuperAdmin: true });
        }
        return;
      }

      // 2. FASE 3: lê tenant do Zustand store (persistido em localStorage)
      //    e verifica se ainda há uma sessão JWT válida no Supabase Auth.
      try {
        // eslint-disable-next-line
        const storeState = require('../store/useAppStore').default.getState();
        const storedTenant   = storeState.tenant;
        const storedTenantId = storeState.tenantId;

        if (storedTenant && storedTenantId) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            // Mostra o tenant do localStorage imediatamente (evita tela de loading)
            if (!cancelled) {
              setState({ tenant: storedTenant, tenantId: storedTenantId, loading: false, isSuperAdmin: false });
            }
            // Rebusca do BD em background para garantir módulos/dados atualizados
            getTenantById(storedTenantId).then(freshTenant => {
              if (!cancelled && freshTenant) {
                setState({ tenant: freshTenant, tenantId: freshTenant.id, loading: false, isSuperAdmin: false });
                // Atualiza o store com dados frescos
                storeState.setTenant?.(freshTenant);
              }
            }).catch(() => { /* silencioso — usa cache se falhar */ });
            return;
          }
          // Sessão expirada → limpa store
          if (session === null) {
            storeState.clearTenant?.();
            storeState.clearUsuario?.();
          }
        }
      } catch (e) {
        // store indisponível ou corrompido — segue sem tenant
      }

      // 3. Sem sessão → mostra login (tenant null, sem bloqueio)
      if (!cancelled) {
        setState({ tenant: null, tenantId: null, loading: false, isSuperAdmin: false });
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  // ── Exposto para useTenantAuth chamar após login/logout ──
  const setTenantData = useCallback((tenant) => {
    setState(prev => ({
      ...prev,
      tenant,
      tenantId: tenant?.id ?? null,
    }));
  }, []);

  const clearTenantData = useCallback(() => {
    setState(prev => ({ ...prev, tenant: null, tenantId: null }));
  }, []);

  // ── Loading inicial ───────────────────────────────────────
  if (state.loading) {
    return <TenantLoadingScreen />;
  }

  return (
    <TenantContext.Provider value={{ ...state, setTenantData, clearTenantData }}>
      {children}
    </TenantContext.Provider>
  );
}

export default TenantContext;
